import { useCallback, useEffect, useRef, useState } from 'react';
import { generateTaskBreakdownStreaming, AIBreakdownIntent } from '../api/ai.api';
import { ChatMessage } from '../components/Modal/AITaskDescription/components/AIDescriptionChat';
import { AITaskBreakdownOptions, SubtaskSuggestion } from '../types/AI';

interface UseAIBreakdownChatOptions {
  workspaceId: number;
  taskTitle: string;
  taskDescription?: string;
  onBreakdownComplete?: (breakdown: SubtaskSuggestion[], reasoning: string) => void;
}

interface UseAIBreakdownChatReturn {
  messages: ChatMessage[];
  userInput: string;
  setUserInput: (value: string) => void;
  sendMessage: (messageText?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  breakdown: SubtaskSuggestion[];
  reasoning: string;
  cached: boolean;
  cancelGeneration: () => void;
  currentMode: AIBreakdownIntent | null;
  modeReasoning: string | null;
  lastCompletedMode: AIBreakdownIntent | null;
  resetChat: () => void;
}

const INITIAL_GREETING = `Hi 👋,
Let me help you break this Item into actionable pieces.

I can propose Subitems for this task from what's already written. You'll review and edit before anything is saved.

Feel free to share constraints (target count, scope, priorities, naming) to guide the first draft.`;

export const useAIBreakdownChat = ({
  workspaceId,
  taskTitle,
  taskDescription,
  onBreakdownComplete,
}: UseAIBreakdownChatOptions): UseAIBreakdownChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<SubtaskSuggestion[]>([]);
  const [reasoning, setReasoning] = useState('');
  const [cached, setCached] = useState(false);
  const [lastAIResponse, setLastAIResponse] = useState('');
  const [currentBreakdown, setCurrentBreakdown] = useState<SubtaskSuggestion[]>([]);

  const messageIdCounter = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamedContentRef = useRef('');
  const streamedChatRef = useRef('');
  const currentModeRef = useRef<AIBreakdownIntent | null>(null);
  const streamingChatMessageIdRef = useRef<string | null>(null);
  const modeNotificationMessageIdRef = useRef<string | null>(null);
  const [lastCompletedMode, setLastCompletedMode] = useState<AIBreakdownIntent | null>(null);

  // Initialize with greeting message
  useEffect(() => {
    const greetingMessage: ChatMessage = {
      id: `msg-${messageIdCounter.current++}`,
      role: 'assistant',
      content: INITIAL_GREETING.split('\n')
        .map((line) => `<p>${line}</p>`)
        .join(''),
      timestamp: new Date(),
    };
    setMessages([greetingMessage]);
  }, []);

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const resetChat = useCallback(() => {
    // Reset chat state with initial greeting
    const greetingMessage: ChatMessage = {
      id: `msg-${messageIdCounter.current++}`,
      role: 'assistant',
      content: INITIAL_GREETING.split('\n')
        .map((line) => `<p>${line}</p>`)
        .join(''),
      timestamp: new Date(),
    };
    setMessages([greetingMessage]);
    setUserInput('');
    setBreakdown([]);
    setReasoning('');
    setCached(false);
    setLastAIResponse('');
    setCurrentBreakdown([]);
    setLastCompletedMode(null);
    setIsLoading(false);
    setError(null);
    // Reset refs
    streamedContentRef.current = '';
    streamedChatRef.current = '';
    currentModeRef.current = null;
    streamingChatMessageIdRef.current = null;
    modeNotificationMessageIdRef.current = null;
    // Cancel any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(
    async (messageText?: string) => {
      const textToSend = messageText || userInput;
      if (!textToSend.trim() || isLoading) return;

      // Add user message to chat
      const userMessage: ChatMessage = {
        id: `msg-${messageIdCounter.current++}`,
        role: 'user',
        content: `<p>${textToSend}</p>`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Only clear input if we're using the state value (not when messageText is provided)
      if (!messageText) {
        setUserInput('');
      }

      setIsLoading(true);
      setError(null);
      streamedContentRef.current = '';
      streamedChatRef.current = '';
      streamingChatMessageIdRef.current = null;

      // Create abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const options: AITaskBreakdownOptions = {
          maxSubtasks: 8,
          includeEstimates: true,
          includeDependencies: false,
          format: 'html',
          detailLevel: 'detailed',
          userFeedback: textToSend,
          // Include last AI response for context (max 1000 chars)
          previousAIResponse: lastAIResponse ? lastAIResponse.substring(0, 1000) : undefined,
          // Include current breakdown for refining existing breakdown
          currentBreakdown: currentBreakdown.length > 0 ? currentBreakdown : undefined,
        };

        await generateTaskBreakdownStreaming(
          {
            workspaceId,
            parentTaskTitle: taskTitle,
            parentTaskDescription: taskDescription,
            options,
          },
          {
            onMode: (mode: AIBreakdownIntent, reasoning: string) => {
              // Store the detected mode
              currentModeRef.current = mode;

              // Add mode notification message to chat
              let modeMessage = '';
              switch (mode) {
                case 'chat':
                  modeMessage = '<p><em>AI will answer your question without modifying the breakdown.</em></p>';
                  break;
                case 'breakdown':
                  modeMessage = '<p><em>AI will update the breakdown based on your feedback.</em></p>';
                  break;
                case 'both':
                  modeMessage = '<p><em>AI will first answer your question, then update the breakdown.</em></p>';
                  break;
              }

              if (modeMessage) {
                const notificationMsgId = `msg-${messageIdCounter.current++}`;
                modeNotificationMessageIdRef.current = notificationMsgId;
                const notificationMsg: ChatMessage = {
                  id: notificationMsgId,
                  role: 'assistant',
                  content: modeMessage,
                  timestamp: new Date(),
                };
                setMessages((prev) => [...prev, notificationMsg]);
              }

              // Create initial streaming message for chat responses
              if (mode === 'chat' || mode === 'both') {
                const streamingMsgId = `msg-${messageIdCounter.current++}`;
                streamingChatMessageIdRef.current = streamingMsgId;
                const streamingMsg: ChatMessage = {
                  id: streamingMsgId,
                  role: 'assistant',
                  content: '',
                  timestamp: new Date(),
                };
                setMessages((prev) => [...prev, streamingMsg]);
                streamedChatRef.current = '';
              }
            },
            onChatChunk: (chunk: string) => {
              // Accumulate chat chunks
              streamedChatRef.current += chunk;

              // Create streaming message if it doesn't exist yet (in case mode event wasn't received)
              if (!streamingChatMessageIdRef.current) {
                const streamingMsgId = `msg-${messageIdCounter.current++}`;
                streamingChatMessageIdRef.current = streamingMsgId;
                const streamingMsg: ChatMessage = {
                  id: streamingMsgId,
                  role: 'assistant',
                  content: streamedChatRef.current,
                  timestamp: new Date(),
                };
                setMessages((prev) => [...prev, streamingMsg]);
              } else {
                // Update the streaming message in real-time
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingChatMessageIdRef.current ? { ...msg, content: streamedChatRef.current } : msg
                  )
                );
              }

              // Store the accumulated chat response for context
              setLastAIResponse(streamedChatRef.current);
            },
            onContentChunk: (chunk: string) => {
              // Accumulate breakdown content chunks
              streamedContentRef.current += chunk;
              // Also store in lastAIResponse for 'content' events
              setLastAIResponse((prev) => prev + chunk);
            },
            onChatDone: () => {
              // Chat phase complete (only in 'both' mode) - finalize the streaming message
              streamingChatMessageIdRef.current = null;

              // Add transition message
              const chatPhaseMessage: ChatMessage = {
                id: `msg-${messageIdCounter.current++}`,
                role: 'assistant',
                content: '<p><em>Now updating the breakdown...</em></p>',
                timestamp: new Date(),
              };
              setMessages((prev) => [...prev, chatPhaseMessage]);
            },
            onComplete: (data) => {
              const mode = data.mode || currentModeRef.current;

              // Store the completed mode
              setLastCompletedMode(mode);

              // Remove mode notification message now that response is complete
              if (modeNotificationMessageIdRef.current) {
                const modeMsgId = modeNotificationMessageIdRef.current;
                setMessages((prev) => prev.filter((msg) => msg.id !== modeMsgId));
                modeNotificationMessageIdRef.current = null;
              }

              // If we had a streaming chat message, finalize it
              streamingChatMessageIdRef.current = null;

              // Update breakdown if provided
              if (data.breakdown) {
                setBreakdown(data.breakdown);
                const breakdownReasoning = data.reasoning || '';
                setReasoning(breakdownReasoning);
                setCached(data.cached || false);

                // Store current breakdown for future refinements
                setCurrentBreakdown(data.breakdown);

                // Add acknowledgment if breakdown was updated
                if (mode === 'breakdown' || mode === 'both') {
                  // Add reasoning as a chat message if available
                  if (breakdownReasoning) {
                    const reasoningMessage: ChatMessage = {
                      id: `msg-${messageIdCounter.current++}`,
                      role: 'assistant',
                      content: `<p>${breakdownReasoning}</p>`,
                      timestamp: new Date(),
                    };
                    setMessages((prev) => [...prev, reasoningMessage]);
                  }

                  const ackMessage: ChatMessage = {
                    id: `msg-${messageIdCounter.current++}`,
                    role: 'assistant',
                    content: `<p>I've updated the breakdown with ${data.breakdown.length} subtask suggestions. Review them on the right and select which ones to create.</p>`,
                    timestamp: new Date(),
                  };
                  setMessages((prev) => [...prev, ackMessage]);

                  // Clear previous AI response after breakdown update
                  setLastAIResponse('');
                }

                onBreakdownComplete?.(data.breakdown, breakdownReasoning);
              }

              // Reset refs for next generation
              streamedChatRef.current = '';
              streamedContentRef.current = '';
              currentModeRef.current = null;
              setIsLoading(false);
              abortControllerRef.current = null;
            },
            onError: (errorMsg: string) => {
              // If there's a streaming message, remove it
              if (streamingChatMessageIdRef.current) {
                const streamingId = streamingChatMessageIdRef.current;
                setMessages((prev) => prev.filter((msg) => msg.id !== streamingId));
                streamingChatMessageIdRef.current = null;
              }

              // Add error message to chat
              const errorMessage: ChatMessage = {
                id: `msg-${messageIdCounter.current++}`,
                role: 'assistant',
                content: `<p style="color: red;">Error: ${errorMsg}</p>`,
                timestamp: new Date(),
              };
              setMessages((prev) => [...prev, errorMessage]);

              // Reset refs on error
              streamedChatRef.current = '';
              streamedContentRef.current = '';
              currentModeRef.current = null;
              setError(errorMsg);
              setIsLoading(false);
              abortControllerRef.current = null;
            },
          },
          controller.signal
        );
      } catch (err: any) {
        if (err.name === 'AbortError') {
          // User cancelled
          if (streamingChatMessageIdRef.current) {
            setMessages((prev) => prev.filter((msg) => msg.id !== streamingChatMessageIdRef.current));
            streamingChatMessageIdRef.current = null;
          }
        } else {
          const errorMsg = err.message || 'Failed to generate breakdown';
          setError(errorMsg);

          const errorMessage: ChatMessage = {
            id: `msg-${messageIdCounter.current++}`,
            role: 'assistant',
            content: `<p style="color: red;">Error: ${errorMsg}</p>`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        }
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [
      userInput,
      isLoading,
      workspaceId,
      taskTitle,
      taskDescription,
      lastAIResponse,
      currentBreakdown,
      onBreakdownComplete,
    ]
  );

  return {
    messages,
    userInput,
    setUserInput,
    sendMessage,
    isLoading,
    error,
    breakdown,
    reasoning,
    cached,
    cancelGeneration,
    currentMode: currentModeRef.current,
    modeReasoning: null, // Mode reasoning is handled inline with notifications
    lastCompletedMode,
    resetChat,
  };
};
